import { GridCellProps } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import TextInput from 'src/components/TextInput';
import { isButtonEnabled } from 'src/components/design-system/Button';
import useDelayedInputOnchange from 'src/hooks/useDelayedInputOnchange';
import { getBgColor } from 'src/utils/icons';
import { round } from 'src/utils/number';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';
import OcrIndicator from '../split_line_item/OcrIndicator';

const ItemTextField: FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const getMatchingOcrLineItem = useContextSelector(
    InvoiceContext,
    (value) => value.getMatchingOcrLineItem
  );
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);

  const { handleChange, value } = useDelayedInputOnchange(props, true);
  const error = useMemo(() => dataItem?.errors?.productItemNum, [dataItem?.errors?.productItemNum]);

  const matchingItem = getMatchingOcrLineItem(dataItem.receiptLineItemId);
  const ocrConfidence = matchingItem.reduce(
    (acc, item) => (acc += parseFloat(item?.item?.itemNumber?.confidence || '0')),
    0
  );
  const averageOcrConfidence = round(ocrConfidence / matchingItem.length, 2);
  const isEdited = useMemo(
    () =>
      matchingItem[0]?.item?.itemNumber?.value?.toLowerCase()?.trim() !==
      value.toLowerCase()?.trim(),
    [matchingItem, value]
  );
  const matchItemIndex = matchingItem.map((match) => match.index);
  const handleHover = useCallback(() => {
    const validIndices = matchingItem
      .filter(
        (match) =>
          Array.isArray(match.item.itemNumber?.boundingBox) &&
          match.item.itemNumber.boundingBox.length > 0
      )
      .map((match) => match.index);
    if (validIndices.length > 0) {
      handleHoverOcrField('itemNumber', validIndices);
    }
  }, [handleHoverOcrField, matchingItem]);
  const tooltip = value.replace('\n', '');

  const showOcrIndicator = useMemo(
    () =>
      initialInvoice?.ocrGenerated &&
      averageOcrConfidence !== 0 &&
      matchingItem.length > 0 &&
      value.trim().length > 0,
    [initialInvoice?.ocrGenerated, averageOcrConfidence, matchingItem.length, value]
  );

  return (
    <GridCellBase {...props} className={getBgColor(error)} disableToolTip>
      <div className="flex flex-row gap-1 items-center justify-between">
        <div className="truncate" data-tooltip-id={'global-tooltip'} data-tooltip-content={tooltip}>
          {Number(dataItem?.poQty) === 0 &&
          isButtonEnabled(dataItem.canDelete) &&
          !isInvoiceEditDisabled ? (
            <TextInput
              type={'text'}
              value={value}
              onChange={handleChange}
              valid={!error?.length}
              onFocus={handleHover}
              ocr-data={`itemNumber:${matchItemIndex}`}
            />
          ) : (
            <span>{value}</span>
          )}
        </div>
        {showOcrIndicator && (
          <OcrIndicator
            isEdited={isEdited}
            ocrConfidence={`${averageOcrConfidence}%`}
            onHover={handleHover}
          />
        )}
      </div>
    </GridCellBase>
  );
};

export default ItemTextField;
